import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Lightbulb, BookOpen, FileSearch, ScrollText, BadgeCheck } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { BRAND_ASSETS } from "@/lib/brandAssets";

const STAGES = [
  { icon: Lightbulb, t: "Upload your project", d: "Upload your class project, internship report, thesis chapter or capstone. We accept Word, PDF, GitHub repos and Drive links." },
  { icon: BookOpen,  t: "Convert with our LMS", d: "Take focused micro-courses on writing, methodology, statistics, ethics and journal targeting — built for working researchers." },
  { icon: FileSearch,t: "Mentor co-writes the paper", d: "A domain mentor restructures, sharpens and co-authors a publication-ready manuscript with you." },
  { icon: ScrollText,t: "Submit & defend reviews", d: "Q1-targeting, response-letter drafting, ethical plagiarism review and resubmission support." },
  { icon: BadgeCheck,t: "Get published", d: "Indexed in your target database, listed on your KhojByte portfolio, ready for grant and PhD applications." },
];

export default function ProjectToPaper() {
  return (
    <div className="min-h-screen bg-white" data-testid="project-to-paper-page">
      <Navbar />
      <main>
        <section className="relative overflow-hidden border-b border-kb-divider">
          <div aria-hidden className="absolute inset-0 kb-kinetic-hex opacity-50" />
          <div className="relative max-w-7xl mx-auto px-6 lg:px-10 pt-20 pb-16 lg:pt-28 grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-purple">PROJECT → PAPER</div>
              <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl text-kb-ink mt-4 leading-[1.05]">
                Turn your class project into a <em className="italic text-kb-purple">published paper</em>.
              </h1>
              <p className="kb-slogan text-base mt-3">Publish Your Idea.</p>
              <p className="mt-6 text-lg text-kb-ink-muted max-w-2xl">
                Bring an unfinished thesis, a strong term project or a capstone — leave with an
                indexed paper. KhojByte mentors and editors guide every step of the conversion.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/register?intent=convert-thesis&next=/dashboard/ideas/new?source=thesis" className="kb-btn-primary" data-testid="ptp-cta-start">
                  Start the conversion <ArrowRight className="w-4 h-4" />
                </Link>
                <Link to="/services" className="kb-btn-outline" data-testid="ptp-cta-services">
                  See full services
                </Link>
              </div>
            </div>
            <div className="lg:col-span-5 relative kb-tilt">
              <img
                src={BRAND_ASSETS.teamLookingLaptop}
                alt="Researchers working on a paper"
                className="rounded-lg shadow-xl w-full object-cover"
              />
            </div>
          </div>
        </section>

        {/* 5-stage roadmap */}
        <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-24">
          <div className="grid lg:grid-cols-12 gap-10 mb-12 items-end">
            <div className="lg:col-span-7">
              <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted mb-3">HOW IT WORKS</div>
              <h2 className="font-serif text-3xl sm:text-4xl text-kb-ink leading-tight">
                Five steps. One <em className="italic text-kb-purple">published</em> outcome.
              </h2>
            </div>
            <p className="lg:col-span-5 text-kb-ink-muted">
              No hand-offs. The same team that teaches the craft also mentors the writing and
              chaperones the submission.
            </p>
          </div>

          <ol className="space-y-4">
            {STAGES.map((s, i) => (
              <li key={s.t} className="kb-card p-6 flex items-start gap-5" data-testid={`ptp-stage-${i}`}>
                <div className="shrink-0 w-12 h-12 rounded-full bg-kb-subtle border border-kb-divider flex items-center justify-center">
                  <s.icon className="w-5 h-5 text-kb-purple" strokeWidth={1.6} />
                </div>
                <div className="flex-1">
                  <div className="flex items-baseline gap-3">
                    <span className="kb-mono text-[10px] tracking-[0.2em] text-kb-ink-light">{String(i + 1).padStart(2, "0")}</span>
                    <div className="font-serif text-xl text-kb-ink">{s.t}</div>
                  </div>
                  <p className="text-sm text-kb-ink-muted mt-1.5 max-w-3xl leading-relaxed">{s.d}</p>
                </div>
              </li>
            ))}
          </ol>
        </section>

        <section className="bg-kb-dark text-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-8">
              <h2 className="font-serif text-3xl lg:text-4xl leading-tight">
                Ready to convert your project?
              </h2>
              <p className="text-white/70 mt-3">A KhojByte mentor will review your upload within 72 hours.</p>
            </div>
            <div className="lg:col-span-4 flex lg:justify-end">
              <Link to="/onboarding/role" className="kb-btn-primary" data-testid="ptp-final-cta">
                Get started <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
